import React, {useEffect, useRef} from 'react';
import {InputAdornment, TextField} from "@mui/material";
import {Box} from "@mui/material";
import CalculateIcon from "@mui/icons-material/Calculate";
import {isAllowedNonNegativeDecimalInput, normalizeNumericInputValue} from "../../../../utils/common/isAllowedDecimalInput";

const TextFieldValidation = ({
                                 id,
                                 placeholder,
                                 children,
                                 select,
                                 type = "text",
                                 name,
                                 value,
                                 label,
                                 disabled,
                                 setCurrValues,
                                 validationError,
                                 helperText,
                                 setValidationState,
                                 adornment,
                                 required = true,
                                 margin = "dense",
                                 height = 70,
                                 setCalculatorOpen,
                                 setPosition,
                                 width='100%',
                                 nonNegative = false,
                                 integerOnly = false,
                                 displayEmpty = false,
                                 selectProps,
                                 variant = "outlined",
                                 inputLabelProps,
                                 onBlur,
                                 onFocus,
                                 onKeyDown,
                             }) => {
    const ref = useRef(null)

    useEffect(() => {
        if (ref.current && setPosition) {
            setPosition(ref.current.getBoundingClientRect());
        }
    }, []);

    useEffect(() => {
        if (type !== "number" || !ref.current) {
            return;
        }
        const input = ref.current.querySelector('input');
        if (!input) {
            return;
        }
        const preventWheelChange = (e) => e.preventDefault();
        input.addEventListener('wheel', preventWheelChange, {passive: false});
        return () => input.removeEventListener('wheel', preventWheelChange);
    }, [type]);

    const onChange = (e) => {
        let inputValue = e.target.value;
        if (nonNegative && !isAllowedNonNegativeDecimalInput(inputValue, integerOnly)) {
            return;
        }
        if (nonNegative) {
            inputValue = normalizeNumericInputValue(value, inputValue, integerOnly);
        }

        if (select && setValidationState) {
            setValidationState((errors) => Object.keys(errors).reduce((acc, it) => ({...acc, [it]: ""}), {}))
        }

        if (validationError) {
            setValidationState((errors) => {
                return {...errors, [name]: ""}
            })
        }
        setCurrValues((values) => {
            return name ? {...values, [name]: inputValue} : inputValue
        })
    }

    const showEndAdornment = Boolean(adornment || setCalculatorOpen);
    const resolvedSelectProps = select
        ? {
            displayEmpty,
            MenuProps: {
                PaperProps: {sx: {maxHeight: 300}},
            },
            ...selectProps,
        }
        : undefined;

    return (<Box sx={{minHeight: height}}>
        <TextField
            autoComplete={"off"}
            ref={ref}
            id={id}
            variant={variant}
            placeholder={select ? undefined : placeholder}
            type={type}
            select={select}
            error={!!validationError}
            helperText={validationError || helperText}
            size={"small"}
            margin={margin}
            sx={{
                width: width,
                gap: '0.01rem',
                '& .MuiFormHelperText-root': {
                    marginTop: '2px',
                    marginBottom: 0,
                    lineHeight: 1.2,
                },
                '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'error.main',
                },
            }}
            required={required}
            label={label}
            InputLabelProps={inputLabelProps}
            name={name}
            disabled={disabled}
            value={value ?? ""}
            onChange={onChange}
            onBlur={onBlur}
            onFocus={onFocus}
            onKeyDown={onKeyDown}
            SelectProps={resolvedSelectProps}
            inputProps={nonNegative && type === "number" ? {min: 0} : undefined}
            InputProps={!select && showEndAdornment ? {
                endAdornment: <InputAdornment
                    position="end" sx={{display: "flex", gap: "5px", "p": {lineHeight: 1}}}>
                    {adornment}
                    {setCalculatorOpen && <CalculateIcon
                        onClick={() => !disabled && setCalculatorOpen(name)}
                        sx={{opacity: disabled ? 0.5 : 1, cursor: disabled ? "auto" : "pointer"}}/>}
                </InputAdornment>,
            } : undefined}
        >{children}</TextField>
    </Box>);
};

export default TextFieldValidation;
